import { useEffect, useState } from 'react'
import { onMemberChange, getMemberId, memberLogout, signInWithGoogle, signInWithApple, getMemberProfile } from '../lib/memberGgc.js'
import { PbccHeroHeader, PBCC_GREEN, PBCC_GOLD } from '../components/PbccLayout.jsx'
import { HUSTLER } from '../config/siteMode.js'
import CLUB from '../config/club.js'

// Two-step access gate for the standalone Golf Game Central — PRODUCTION ONLY.
//  1. ONE-TIME MEMBERS ACCESS CODE — a single shared code, entered once and
//     remembered locally (localStorage), so returning members skip straight past.
//  2. SIGN IN WITH APPLE / GOOGLE — Firebase Auth OAuth. The user's Apple/Google
//     account IS their account (uid = provider uid); getMemberId() returns it, so
//     saved games load from the SAME per-user backend the companion app uses.
// The old Member-ID field is gone. Staged so it never blanks the site: the gate
// screens are pure UI, onMemberChange is wrapped, App.jsx has an ErrorBoundary.
//
// HOST-BASED: the SAME --mode ggc build is deployed to both the test host
// (pbcc-ggc-test) and production (pbcc). The gate must NEVER appear on the test
// host — that's Mark's working site, wide open. So the gate is skipped on the
// test host + localhost and only renders on production (pbcc + any real domain).
const NO_GATE_HOSTS = new Set(['pbcc-ggc-test.web.app', 'localhost', '127.0.0.1'])
const GATE_DISABLED = (() => {
  try { return typeof window !== 'undefined' && NO_GATE_HOSTS.has(window.location.hostname) } catch { return false }
})()

// The shared access code is baked into the build (client-side check — it only
// keeps casual visitors out; the REAL identity is the Apple/Google sign-in).
// Change it via VITE_GGC_CLUB_CODE in the build env.
const CLUB_CODE = String(import.meta.env.VITE_GGC_CLUB_CODE || 'est2026').trim().toLowerCase()
const UNLOCK_KEY = 'ggcClubUnlocked'
const isUnlocked = () => { try { return localStorage.getItem(UNLOCK_KEY) === '1' } catch { return false } }
const BODY_GREEN = '#15231B'
const cream = CLUB.creamColor || '#F3ECD9'

export default function GgcMemberGate({ children }) {
  // Test host / localhost = wide open, exactly as before — no gate at all.
  // (No hooks here, so this early return is always safe.)
  if (GATE_DISABLED) return children
  // Golf Hustler build: sign-in REQUIRED (Apple/Google — the SAME buttons PBCC
  // uses), but NO PBCC members-access-code step. Signing in is what lets a user
  // set up/save/send a game to the app; the Apple/Google uid is the same one the
  // Golf Hustler app gets (same completetourneylive Apple/Google provider).
  if (HUSTLER) return <GatedApp requireCode={false}>{children}</GatedApp>
  // PBCC: unchanged — one-time members access code, then Apple/Google sign-in.
  return <GatedApp requireCode>{children}</GatedApp>
}

function GatedApp({ children, requireCode = true }) {
  // Hustler build passes requireCode=false → no access-code screen, straight to
  // the Apple/Google sign-in. PBCC passes requireCode (true) → code first.
  const [unlocked, setUnlocked] = useState(() => (requireCode ? isUnlocked() : true))
  const [uid, setUid] = useState(() => { try { return getMemberId() } catch { return null } })
  useEffect(() => {
    let off = () => {}
    try { off = onMemberChange((id) => setUid(id)) } catch { /* auth unavailable → stay on gate, never blank */ }
    return () => { try { off() } catch {} }
  }, [])

  if (requireCode && !unlocked) return <ClubCode onUnlock={() => setUnlocked(true)} />
  if (!uid) return <SignIn />

  const profile = (() => { try { return getMemberProfile() } catch { return null } })()
  const who = profile?.name || profile?.email || 'Signed in'
  return (
    <>
      <div style={{ background: PBCC_GREEN, color: '#E2F4C5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', fontSize: 13 }}>
        <span>{who}</span>
        <button onClick={() => { try { memberLogout() } catch {} }}
          style={{ background: 'transparent', color: PBCC_GOLD, border: `1px solid ${PBCC_GOLD}`, borderRadius: 8, padding: '4px 12px', fontWeight: 700, cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
      {children}
    </>
  )
}

// Fixed, opaque, top-most branded overlay shared by both gate screens.
function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto', background: BODY_GREEN }}>
      <PbccHeroHeader />
      <div style={{ maxWidth: 550, margin: '0 auto', padding: '28px 20px 64px', position: 'relative', zIndex: 2 }}>
        <div style={{ background: cream, borderRadius: 16, border: `1px solid ${PBCC_GOLD}66`, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', padding: '30px 22px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Cinzel, serif', fontVariant: 'small-caps', fontWeight: 600, letterSpacing: '0.06em', fontSize: 25, color: PBCC_GREEN, lineHeight: 1.1 }}>Golf Game Central</div>
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Step 1: one-time Members Access Code ─────────────────────────────────────
function ClubCode({ onUnlock }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const submit = (e) => {
    e?.preventDefault?.()
    if (String(code).trim().toLowerCase() === CLUB_CODE) {
      try { localStorage.setItem(UNLOCK_KEY, '1') } catch {}
      onUnlock()
    } else {
      setErr('That access code is not correct.')
    }
  }
  return (
    <Overlay>
      <div style={{ marginTop: 8, marginBottom: 22, fontSize: 14, color: '#5C7562' }}>Enter your Members Access Code to continue</div>
      <form onSubmit={submit}>
        <input value={code} onChange={(e) => { setCode(e.target.value); setErr('') }} autoFocus placeholder="Members Access Code"
          style={{ width: '100%', boxSizing: 'border-box', background: '#fff', border: `1px solid ${PBCC_GREEN}33`, borderRadius: 12, padding: '14px 16px', fontSize: 18, color: PBCC_GREEN, textAlign: 'center', letterSpacing: 1 }} />
        {err ? <div style={{ color: '#B42318', fontSize: 13, marginTop: 12 }}>{err}</div> : null}
        <button type="submit"
          style={{ width: '100%', marginTop: 18, background: PBCC_GOLD, color: PBCC_GREEN, border: 'none', borderRadius: 12, padding: '15px', fontSize: 17, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
          Unlock
        </button>
      </form>
      <div style={{ marginTop: 18, fontSize: 12, color: '#7C8A80' }}>One-time for this device. Ask the pro shop for the code.</div>
    </Overlay>
  )
}

// ── Step 2: sign in with Apple / Google ──────────────────────────────────────
function SignIn() {
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const go = async (which) => {
    if (busy) return
    setErr(''); setBusy(which)
    const res = which === 'apple' ? await signInWithApple() : await signInWithGoogle()
    setBusy('')
    // Success: onMemberChange flips the gate to the app. Otherwise show a reason.
    if (!res.ok) {
      const r = res.reason || ''
      setErr(r.includes('popup-closed') || r.includes('cancelled') ? 'Sign-in was cancelled.'
        : r.includes('operation-not-allowed') ? 'This sign-in isn’t enabled yet — the club is still setting it up.'
        : r.includes('popup-blocked') ? 'Your browser blocked the popup — allow popups and try again.'
        : r.includes('unauthorized-domain') ? 'This site isn’t authorized for sign-in yet — the club is setting it up.'
        : 'Sign-in failed — please try again.')
    }
  }
  return (
    <Overlay>
      <div style={{ marginTop: 8, marginBottom: 22, fontSize: 14, color: '#5C7562' }}>Sign in to load your games</div>

      <button onClick={() => go('apple')} disabled={!!busy}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#000', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer', opacity: busy && busy !== 'apple' ? 0.5 : 1 }}>
        <AppleLogo /> {busy === 'apple' ? 'Signing in…' : 'Sign in with Apple'}
      </button>

      <button onClick={() => go('google')} disabled={!!busy}
        style={{ width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#fff', color: '#3c4043', border: '1px solid #dadce0', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer', opacity: busy && busy !== 'google' ? 0.5 : 1 }}>
        <GoogleLogo /> {busy === 'google' ? 'Signing in…' : 'Sign in with Google'}
      </button>

      {err ? <div style={{ color: '#B42318', fontSize: 13, marginTop: 14 }}>{err}</div> : null}
      <div style={{ marginTop: 18, fontSize: 12, color: '#7C8A80' }}>Your Apple or Google account is your account. Your games sync to it.</div>
    </Overlay>
  )
}

function AppleLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 384 512" fill="#fff" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  )
}
function GoogleLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}
